'use client';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Sparkles, Megaphone, BarChart3, Bot, Loader2, X, Minus, Maximize2 } from 'lucide-react';
import { apiService } from '@/lib/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MARKDOWN_PLUGINS = [remarkGfm];

const MARKDOWN_COMPONENTS = {
  p: ({ node, ...props }) => <p className="mb-2 last:mb-0 leading-relaxed text-slate-800" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc ml-4 mb-2 space-y-1 text-slate-800" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal ml-4 mb-2 space-y-1 text-slate-800" {...props} />,
  li: ({ node, ...props }) => <li className="mb-0.5" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-extrabold text-slate-900" {...props} />,
  a: ({ node, ...props }) => <a className="text-blue-600 underline hover:text-blue-800 font-bold" target="_blank" rel="noopener noreferrer" {...props} />,
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-2.5 rounded-xl border border-gray-200 shadow-xs bg-white">
      <table className="w-full text-xs text-left border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[10px] tracking-wider border-b border-gray-200" {...props} />,
  tbody: ({ node, ...props }) => <tbody className="divide-y divide-gray-100" {...props} />,
  tr: ({ node, ...props }) => <tr className="hover:bg-slate-50/80 transition-colors" {...props} />,
  th: ({ node, ...props }) => <th className="px-2.5 py-2 font-bold whitespace-nowrap text-slate-800 border-r border-gray-200 last:border-r-0" {...props} />,
  td: ({ node, ...props }) => <td className="px-2.5 py-1.5 whitespace-nowrap text-slate-700 border-r border-gray-100 last:border-r-0" {...props} />,
};

const normalizeMarkdownTables = (text) => {
  if (!text || typeof text !== 'string') return text;

  let fixed = text.replace(/\|\s*\|\s*/g, '|\n| ');

  const lines = fixed.split('\n');
  const result = [];
  let inTable = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const isTableRow = trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 2;

    if (isTableRow) {
      inTable = true;
      result.push(trimmed);
    } else if (trimmed === '' && inTable) {
      let nextIsTable = false;
      for (let j = i + 1; j < lines.length; j++) {
        const nextTrimmed = lines[j].trim();
        if (nextTrimmed === '') continue;
        if (nextTrimmed.startsWith('|') && nextTrimmed.endsWith('|')) {
          nextIsTable = true;
        }
        break;
      }
      if (nextIsTable) {
        continue;
      } else {
        inTable = false;
        result.push(line);
      }
    } else {
      inTable = false;
      result.push(line);
    }
  }

  return result.join('\n');
};

const AssistantChatMessage = React.memo(function AssistantChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  const content = useMemo(() => {
    return isUser ? msg.content : normalizeMarkdownTables(msg.content);
  }, [isUser, msg.content]);

  return (
    <div className={`
      max-w-[85%] p-3.5 rounded-2xl text-[14px] leading-relaxed shadow-sm overflow-hidden
      ${isUser 
      ? 'self-end bg-blue-600 text-white rounded-br-none' 
      : 'self-start bg-white text-gray-800 border border-gray-100 rounded-bl-none'}
    `}>
      {isUser ? (
        content
      ) : (
        <div className="prose prose-sm max-w-none text-inherit">
          <ReactMarkdown
            remarkPlugins={MARKDOWN_PLUGINS}
            components={MARKDOWN_COMPONENTS}
          >
            {content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
});

const AssistantChatInput = React.memo(function AssistantChatInput({ onSend, isTyping, externalInput, onClearExternalInput }) {
  const [input, setInput] = useState('');

  useEffect(() => {
    if (externalInput) {
      setInput(externalInput);
      if (onClearExternalInput) onClearExternalInput();
    }
  }, [externalInput, onClearExternalInput]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || isTyping) return;
    onSend(input);
    setInput('');
  };

  return (
    <form onSubmit={handleSubmit} className="p-3 border-t border-gray-200 bg-white">
      <div className="flex gap-2 bg-gray-100 p-1.5 rounded-xl items-center focus-within:ring-2 ring-blue-100 transition-all">
        <input 
          type="text" 
          placeholder="Pregúntale a Baly sobre tus campañas..." 
          className="flex-grow bg-transparent border-none outline-none text-sm px-3 py-1.5 text-gray-800"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <button 
          type="submit"
          disabled={!input.trim() || isTyping}
          className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 cursor-pointer"
        >
          <Send size={18} />
        </button>
      </div>
    </form>
  );
});

export default function BalyMarketingAssistant({ onClose, isMinimized, setIsMinimized }) {
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: '¡Hola! Soy Baly, tu gestor de marketing. Estoy conectado al protocolo MCP de Meta Ads. ¿Qué deseas optimizar hoy?' 
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [actionInput, setActionInput] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current && !isMinimized) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isMinimized]);

  const handleSend = useCallback(async (text) => {
    if (!text || !text.trim()) return;

    const userMessage = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setIsTyping(true);

    try {
        const response = await apiService.post('/marketing/chat', {
            message: text,
            history: messages,
            ai_tolerance: 0.7
        });

        if (response && response.response) {
            setMessages([...newMessages, { role: 'assistant', content: response.response }]);
        } else {
            setMessages([...newMessages, { role: 'assistant', content: 'Lo siento, hubo un error procesando tu solicitud MCP.' }]);
        }
    } catch (error) {
        console.error("Error Baly Marketing MCP:", error);
        setMessages([...newMessages, { role: 'assistant', content: 'Lo siento, hubo un error de conexión con Meta Ads o el servidor.' }]);
    } finally {
        setIsTyping(false);
    }
  }, [messages]);

  const SuggestedAction = ({ icon, text }) => (
    <button 
      onClick={() => setActionInput(text)}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-bold text-gray-600 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all shadow-sm whitespace-nowrap cursor-pointer"
    >
      {icon}
      {text}
    </button>
  );

  return (
    <div className={`fixed bottom-6 right-6 w-[768px] max-w-[95vw] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 flex flex-col transition-all overflow-hidden ${isMinimized ? 'h-14' : 'h-[600px]'}`}>
      {/* Header */}
      <div 
        className="p-3 bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-between cursor-pointer"
        onClick={() => isMinimized && setIsMinimized(false)}
      >
        <div className="flex items-center gap-3">
            <div className="bg-white/20 p-1.5 rounded-lg">
            <Bot size={20} />
            </div>
            <div>
            <h4 className="font-bold text-sm m-0 leading-none mb-0.5">Baly Marketing</h4>
            <div className="flex items-center gap-1.5 text-[9px] opacity-90 uppercase tracking-widest font-black">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse shadow-[0_0_5px_rgba(74,222,128,0.8)]"></span>
                Conectado vía MCP
            </div>
            </div>
        </div>
        <div className="flex items-center gap-1">
            <button onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }} className="hover:bg-white/20 p-1.5 rounded-md transition-colors">
                {isMinimized ? <Maximize2 size={16} /> : <Minus size={16} />}
            </button>
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="hover:bg-red-500 p-1.5 rounded-md transition-colors">
                <X size={16} />
            </button>
        </div>
      </div>

      {!isMinimized && (
          <>
            {/* Messages */}
            <div 
                ref={scrollRef}
                className="flex-grow p-5 overflow-y-auto flex flex-col gap-4 bg-gray-50/50"
            >
                {messages.map((msg, i) => (
                  <AssistantChatMessage key={i} msg={msg} />
                ))}
                {isTyping && (
                <div className="self-start p-3 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <Loader2 size={18} className="animate-spin text-blue-600" />
                </div>
                )}
            </div>

            {/* Actions */}
            <div className="px-5 py-3 flex gap-2 overflow-x-auto no-scrollbar border-t border-gray-100 bg-gray-50">
                <SuggestedAction icon={<Megaphone size={14} />} text="Crear campaña de leads" />
                <SuggestedAction icon={<BarChart3 size={14} />} text="Reporte de este mes" />
                <SuggestedAction icon={<Sparkles size={14} />} text="Optimizar anuncios" />
            </div>

            {/* Input Aislado */}
            <AssistantChatInput 
              onSend={handleSend}
              isTyping={isTyping}
              externalInput={actionInput}
              onClearExternalInput={() => setActionInput('')}
            />
          </>
      )}
    </div>
  );
}
